import { Drop } from "../types";
import { CLASSICS } from "../data/classics";
import { FONTS, GRAD, PINK, CREAM, CARD_BORDER } from "../data/affirmations";
import { RouteMap } from "./RouteMap";
import { useLocation } from "../hooks/useLocation";

interface ClassicsTabProps {
  onStartWalk: (drop: Drop) => void;
}

function getDistStyle(dist: number) {
  if (dist < 1) return { background: "#F0FFF4", border: "1px solid #C8E6C9", color: "#0A7C5E" };
  if (dist < 3) return { background: "#FFF8F0", border: "1px solid #FFD9A8", color: "#B5621E" };
  return { background: "#F5F5F5", border: "1px solid #E0E0E0", color: "#999" };
}

function getDistLabel(dist: number) {
  if (dist < 0.1) return "You are here!";
  return dist.toFixed(1) + " mi away";
}

export function ClassicsTab({ onStartWalk }: ClassicsTabProps) {
  const { userLocation, locationLoading, locationError, getLocation, getDistanceMiles } = useLocation();

  const sortedClassics = userLocation
    ? [...CLASSICS].sort((a, b) => {
        const distA = getDistanceMiles(userLocation.lat, userLocation.lng, a.waypoints[0].lat, a.waypoints[0].lng);
        const distB = getDistanceMiles(userLocation.lat, userLocation.lng, b.waypoints[0].lat, b.waypoints[0].lng);
        return distA - distB;
      })
    : CLASSICS;

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <style>{FONTS + `@keyframes fadeUp { from { opacity:0;transform:translateY(16px) } to { opacity:1;transform:translateY(0) } }`}</style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div>
          <div style={{ fontSize: "9px", letterSpacing: "5px", color: PINK, textTransform: "uppercase", fontWeight: 700, fontFamily: "'Poppins',sans-serif", marginBottom: "4px" }}>always available</div>
          <div style={{ fontFamily: "'Poppins',sans-serif", fontSize: "28px", fontWeight: 900, color: "#1a1a1a", letterSpacing: "-1px", lineHeight: 1 }}>Classic Walks</div>
        </div>
        <button
          onClick={getLocation}
          style={{ background: GRAD, border: "none", borderRadius: "50px", padding: "10px 16px", fontSize: "12px", fontWeight: 700, color: "#fff", fontFamily: "'Poppins',sans-serif", cursor: "pointer", boxShadow: "0 4px 12px rgba(255,105,180,0.3)" }}>
          {locationLoading ? "Finding you..." : userLocation ? "Updated" : "Find me"}
        </button>
      </div>

      {locationError && (
        <div style={{ background: "#FFF0F8", border: `1px solid ${CARD_BORDER}`, borderRadius: "14px", padding: "12px 16px", marginBottom: "16px", fontSize: "13px", color: PINK, fontFamily: "'Poppins',sans-serif" }}>
          {locationError}
        </div>
      )}

      {!userLocation && !locationLoading && (
        <div style={{ background: "#FFF0F8", border: `1px solid ${CARD_BORDER}`, borderRadius: "20px", padding: "20px", marginBottom: "20px", textAlign: "center" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>📍</div>
          <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, fontSize: "17px", color: "#1a1a1a", marginBottom: "6px" }}>Share your location</div>
          <div style={{ fontSize: "13px", color: "#999", fontFamily: "'Poppins',sans-serif", marginBottom: "16px", lineHeight: 1.6 }}>
            We will sort these by how close they are to you right now
          </div>
          <button
            onClick={getLocation}
            style={{ padding: "12px 28px", borderRadius: "50px", border: "none", cursor: "pointer", background: GRAD, color: "#fff", fontSize: "13px", fontWeight: 700, fontFamily: "'Poppins',sans-serif", boxShadow: "0 4px 16px rgba(255,105,180,0.3)" }}>
            Share Location
          </button>
        </div>
      )}

      {sortedClassics.map(classic => {
        const nearbyDist = userLocation
          ? getDistanceMiles(userLocation.lat, userLocation.lng, classic.waypoints[0].lat, classic.waypoints[0].lng)
          : null;

        const distStyle = nearbyDist !== null ? getDistStyle(nearbyDist) : null;
        const distLabel = nearbyDist !== null ? getDistLabel(nearbyDist) : "";

        const drop: Drop = {
          id: classic.id,
          title: classic.title,
          subtitle: classic.subtitle,
          inspo: classic.vibe,
          emoji: classic.emoji,
          cardBg: classic.cardBg,
          cardBorder: classic.cardBorder,
          accentColor: classic.accentColor,
          tagBg: classic.tagBg,
          distance: classic.distance,
          time: classic.time,
          neighbourhood: classic.neighbourhood,
          landmarks: classic.waypoints.map(w => w.label),
          playlistName: classic.playlistName,
          playlistEmoji: classic.playlistEmoji,
          spotifyUrl: classic.spotifyUrl,
          songs: classic.songs,
          bpm: classic.bpm,
          isLimited: false,
          expiresIn: "",
          tag: "CLASSIC",
          waypoints: classic.waypoints,
        };

        return (
          <div key={classic.id} style={{ background: "#fff", borderRadius: "18px", overflow: "hidden", marginBottom: "10px", border: `1px solid ${CARD_BORDER}` }}>
            <div style={{ height: "3px", background: GRAD }} />
            <div style={{ padding: "16px 18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                <span style={{ fontSize: "8px", fontWeight: 700, letterSpacing: "2px", color: PINK, textTransform: "uppercase", fontFamily: "'Poppins',sans-serif" }}>Classic</span>
                {distStyle && (
                  <span style={{ ...distStyle, fontSize: "10px", fontWeight: 700, padding: "3px 10px", borderRadius: "50px", fontFamily: "'Poppins',sans-serif" }}>
                    {distLabel}
                  </span>
                )}
              </div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#1a1a1a", letterSpacing: "-0.5px", lineHeight: 1.1, fontFamily: "'Poppins',sans-serif", marginBottom: "3px" }}>{classic.title}</div>
              <p style={{ fontSize: "12px", color: "#999", fontFamily: "'Poppins',sans-serif", marginBottom: "14px" }}>{classic.subtitle}</p>

              <div style={{ marginBottom: "14px" }}>
                <RouteMap drop={drop} compact={true} />
              </div>

              <div style={{ display: "flex", paddingTop: "12px", borderTop: `1px solid ${CARD_BORDER}`, marginBottom: "14px" }}>
                {[
                  { label: "Distance", val: classic.distance },
                  { label: "Time", val: classic.time },
                  { label: "Steps", val: classic.steps },
                ].map((s, i) => (
                  <div key={s.label} style={{ flex: 1, borderRight: i < 2 ? `1px solid ${CARD_BORDER}` : "none", paddingRight: i < 2 ? "12px" : "0", paddingLeft: i > 0 ? "12px" : "0" }}>
                    <div style={{ fontSize: "17px", fontWeight: 800, color: "#1a1a1a", fontFamily: "'Poppins',sans-serif" }}>{s.val}</div>
                    <div style={{ fontSize: "9px", color: "#bbb", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "'Poppins',sans-serif" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: CREAM, border: `1px solid ${CARD_BORDER}`, borderRadius: "14px", padding: "12px 14px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ fontSize: "24px" }}>{classic.playlistEmoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "9px", color: "#bbb", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Poppins',sans-serif" }}>paired playlist</div>
                  <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: "14px", color: "#1a1a1a" }}>{classic.playlistName}</div>
                  <div style={{ fontSize: "11px", color: "#999", fontFamily: "'Poppins',sans-serif" }}>{classic.bpm}</div>
                </div>
                <button
                  onClick={() => window.open(classic.spotifyUrl, "_blank")}
                  style={{ background: "#1DB954", borderRadius: "50px", padding: "7px 14px", color: "#000", fontWeight: 700, fontSize: "12px", border: "none", cursor: "pointer", fontFamily: "'Poppins',sans-serif" }}>
                  Play
                </button>
              </div>

              <button
                onClick={() => onStartWalk(drop)}
                style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "none", cursor: "pointer", background: GRAD, color: "#fff", fontSize: "13px", fontWeight: 700, fontFamily: "'Poppins',sans-serif", boxShadow: "0 6px 20px rgba(255,105,180,0.3)" }}>
                Slay This Walk ✨
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
